import TestsDataStorage from '@/Utils/TestsDataStorage.js';
import { useProjectStore } from '@/Stores/Web/projectStore';
import { useCurrencyStore } from '@/Stores/Web/currencyStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';
import { getAppPlatformCookie } from '@/Utils/PwaUtility';
import { getAdvertisingConsent } from '@/Utils/PrivacyUtility';
import Config from '@/Config';

const analyticsPlatformPerPlatform = {
    'gamefound': 'gamefound',
    'ideafound': 'ideafound',
};

const analyticsPlatformAdsPerPlatform = {
    'gamefound': 'gamefound-ads',
    'ideafound': 'ideafound-ads',
};

const platformNameLowercase = Config.platformName.toLowerCase();

// Facebook Pixel Standard Events https://www.facebook.com/business/help/402791146561655
// Default Google Analytics Events  https://developers.google.com/analytics/devguides/collection/gtagjs/events#default_google_analytics_events - technically this for gtag.js but it's basically the same thing and we might upgrade someday

export const googleAnalyticsGroups = {
    platform: analyticsPlatformPerPlatform[platformNameLowercase], // Global analiticys group send to platform trackers (UA, GA4)
    creator: 'creator', // Creator group send to creator trackers (UA, GA4)
    platformAds: analyticsPlatformAdsPerPlatform[platformNameLowercase], // Globals google ads
};

// TODO: Rewrite this class in task #6315
export default class AnalyticsTracking {
    constructor() {
        const projectStore = useProjectStore();
        const projectContextStore = useProjectContextStore();
        this.analyticsConfig = projectStore.analyticsConfig;
        this.projectTrackingModel = projectStore.projectTrackingModel || {};

        const platformCookie = getAppPlatformCookie();
        this.platformData = {
            'platform_name': platformCookie.platformName,
            'platform_type': platformCookie.platformType,
        };

        this.isEnabled = !!this.analyticsConfig && getAdvertisingConsent() && !projectContextStore.projectContext.isInTestMode;
    }

    // TODO: We could send payload with custom data here but we don't have any specification for now
    sendCustomEvent({
        category, // The category of the event.
        action, // The value that will appear as the event action in Google Analytics Event reports.
        label,
        nonInteraction = false,
        value = null, // A non-negative integer that will appear as the event value.
        sendTo = [googleAnalyticsGroups.platform, googleAnalyticsGroups.creator],
    }) {
        if (!this.isEnabled) {
            return;
        }

        if (category && action) {
            if (this.analyticsConfig.googleAnalytics) {
                window.gtag('event', action, {
                    'send_to': sendTo,
                    'event_category': category,
                    'event_label': label,
                    value,
                    'non_interaction': nonInteraction,
                    ...this.platformData,
                });
            }
        }
        else {
            throw 'Category and action arguments are required.';
        }

        TestsDataStorage.setItem('customEvent', {
            category,
            action,
            label,
            nonInteraction,
            value,
            ...this.platformData,
        });
    }

    addToCartTracking(orderItem) {
        if (!this.isEnabled) {
            return;
        }
        const { googleAds } = this.projectTrackingModel;
        const currencyStore = useCurrencyStore();
        const currentCurrency = currencyStore.currentCurrency;

        if (this.analyticsConfig.facebookPixel) {
            this.trackFb('AddToCart', {
                content_name: `[${ this.analyticsConfig.project.name }] ${ orderItem.displayName }`,
                content_type: 'product',
                value: orderItem.quantity * orderItem.priceConverted,
                currency: currentCurrency.shortName,
                contents: [
                    {
                        id: `${ orderItem.productID }`,
                        quantity: orderItem.quantity,
                        item_price: orderItem.priceConverted,
                    },
                ],
            });
        }

        if (this.analyticsConfig.googleAnalytics) {
            window.gtag('event', 'add_to_cart', {
                items: [
                    {
                        id: orderItem.productID,
                        name: `[${ this.analyticsConfig.project.name }] ${ orderItem.displayName }`,
                        variant: orderItem.options.map(o => o.valueText).join(', '),
                        quantity: orderItem.quantity,
                        price: orderItem.priceConverted,
                    },
                ],
                ...this.platformData,
                event_label: this.analyticsConfig.project.urlName,
                send_to: [googleAnalyticsGroups.platform, googleAnalyticsGroups.creator],
            });

            if (googleAds?.enabled && !!googleAds?.addToCartEventID) {
                window.gtag('event', 'conversion', {
                    send_to: [googleAds.addToCartEventID, googleAnalyticsGroups.platformAds],
                    value: orderItem.priceConverted,
                    currency: currentCurrency.shortName,
                    ...this.platformData,
                });
            }
        }

        TestsDataStorage.setItem('addToCart', orderItem);
    }

    registrationCompletedTracking({ email, value, currency }) {
        if (!this.isEnabled) {
            return;
        }

        if (this.analyticsConfig.googleAnalytics) {
            window.gtag('event', 'sign_up', {
                send_to: [googleAnalyticsGroups.platform],
                ...this.platformData,
            });
        }

        if (this.analyticsConfig.facebookPixel) {
            this.trackFb('CompleteRegistration', {
                // TODO: This is against our privacy policy
                content_name: email,
                value,
                currency,
            });
        }

        TestsDataStorage.setItem('registrationCompleted', { email, value, currency, ...this.platformData });
    }

    followTracking({ projectName, source = 'Button' }) {
        if (!this.isEnabled) {
            return;
        }

        const { googleAds } = this.projectTrackingModel;
        this.sendCustomEvent({
            category: 'project',
            action: 'follow',
            label: `${ source }:${ projectName }`,
            ...this.platformData,
        });

        if (this.analyticsConfig.facebookPixel) {
            // Custom event names must be strings, and cannot exceed 50 characters in length.
            // https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking/ - Tracking Custom Events
            this.trackFbCustom('follow', {
                category: 'project',
                label: `${ source }:${ projectName }`,
            });
        }

        if (this.analyticsConfig.googleAnalytics && googleAds?.enabled && !!googleAds?.followEventID) {
            window.gtag('event', 'conversion', {
                send_to: [googleAds.followEventID, googleAnalyticsGroups.platformAds],
                ...this.platformData,
            });
        }
        TestsDataStorage.setItem('follow', { projectName, ...this.platformData });
    }

    unfollowTracking({ projectName, source = 'Button' }) {
        if (!this.isEnabled) {
            return;
        }

        this.sendCustomEvent({
            category: 'project',
            action: 'unfollow',
            label: `${ source }:${ projectName }`,
            ...this.platformData,
        });

        if (this.analyticsConfig.facebookPixel) {
            // Custom event names must be strings, and cannot exceed 50 characters in length.
            // https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking/ - Tracking Custom Events
            this.trackFbCustom('unfollow', {
                category: 'project',
                label: `${ source }:${ projectName }`,
            });
        }

        TestsDataStorage.setItem('unfollow', { projectName });
    }

    purchaseTracking(transaction) {
        if (!this.isEnabled) {
            return;
        }

        const { googleAds } = this.projectTrackingModel;
        const orderLocalKey = `${Config.platformName}.Tracking.OrderCode`;
        const orderLocalCode = sessionStorage.getItem(orderLocalKey);

        if (this.analyticsConfig.googleAnalytics && orderLocalCode !== transaction.transactionId) {
            window.gtag('event', 'purchase', {
                event_label: transaction.projectUrlName,
                transaction_id: transaction.transactionId,
                affiliation: transaction.affiliation,
                value: transaction.revenue,
                currency: transaction.currency,
                shipping: transaction.shipping,
                items: transaction.items,
                metric1: transaction.metric1,
                send_to: [googleAnalyticsGroups.platform, googleAnalyticsGroups.creator],
                ...this.platformData,
            });

            if (googleAds?.enabled && !!googleAds?.purchaseEventID) {
                window.gtag('event', 'conversion', {
                    send_to: [googleAds.purchaseEventID, googleAnalyticsGroups.platformAds],
                    value: transaction.revenue,
                    currency: transaction.currency,
                    transaction_id: transaction.transactionId,
                    ...this.platformData,
                });
            }

            sessionStorage.setItem(orderLocalKey, transaction.transactionId);
        }

        if (this.analyticsConfig.facebookPixel) {
            this.trackFb('Purchase', {
                content_type: 'product',
                value: transaction.revenue,
                currency: transaction.currency,
                contents: transaction.items.map(item => ({
                    id: item.productId,
                    item_price: item.price,
                    quantity: item.quantity,
                })),
            });
        }

        TestsDataStorage.setItem('purchase', transaction);
    }

    getPixelsToTrack() {
        return window.facebookPixels
            .filter(c => c.shouldTrackEvents)
            .map(c => c.code);
    }

    trackFb(eventName, params) {
        const pixels = this.getPixelsToTrack();
        for (const pixelId of pixels) {
            // we have to do .toString() because otherwise facebook treats number as undefined
            // IMPORTANT: problems related to invalid pixel code show up as warnings not errors. If you have warnings filtered out you will not see them
            window.fbq('trackSingle', pixelId.toString(), eventName, params);
        }
    }

    trackFbCustom(action, params) {
        const pixels = this.getPixelsToTrack();
        for (const pixelId of pixels) {
            // we have to do .toString() because otherwise facebook treats number as undefined
            // IMPORTANT: problems related to invalid pixel code show up as warnings not errors. If you have warnings filtered out you will not see them
            window.fbq('trackSingleCustom', pixelId.toString(), action, params);
        }
    }
}
